import { DocumentType } from '../../../types/Podcast';
import {
  DocumentSortOptionsEnum,
  DocumentSortOrderEnum,
  DocumentSortValueEnum
} from '../../documents/types/types';
import { firebase } from '../../../firebase';
import { fetchDocuments } from '../../../apis/documents';
import {
  getDocumentsWithUniqueIdentifier,
  getOnlyFullDocuments
} from './helpers';
import { hasMoreDocumentsCheck } from '../../../utils/hasMoreCheckUtil';
import { FetchDocumentsReasonEnum } from '../types/enums';
import { setAllSeasons } from '../../../redux/actions/player-actions';
import { setPlaybackPlaylists } from '../../../redux/actions/app-actions';
import { DOCUMENTS_PAGE_SIZE } from '../../../constants/constants';

/**
 * Handle the change of the sort order for documents.
 *
 * @param {string} option - Selected sort option.
 * @param {function} setDocumentSortOption - Function to set the document sort option.
 * @param {function} setDocumentSortValue - Function to set the document sort value.
 * @param {function} setDocumentSortOrder - Function to set the document sort order.
 */
export function changeSortOrder(
  option: string,
  setDocumentSortOption: (option: DocumentSortOptionsEnum) => void,
  setDocumentSortValue: (value: DocumentSortValueEnum) => void,
  setDocumentSortOrder: (order: DocumentSortOrderEnum) => void
) {
  switch (option) {
    case DocumentSortOptionsEnum.DEFAULT:
      setDocumentSortOption(DocumentSortOptionsEnum.DEFAULT);
      break;
    case DocumentSortOptionsEnum.NEWEST:
      setDocumentSortOption(DocumentSortOptionsEnum.NEWEST);
      setDocumentSortValue(DocumentSortValueEnum.DATE);
      setDocumentSortOrder(DocumentSortOrderEnum.DESC);
      break;
    case DocumentSortOptionsEnum.OLDEST:
      setDocumentSortOption(DocumentSortOptionsEnum.OLDEST);
      setDocumentSortValue(DocumentSortValueEnum.DATE);
      setDocumentSortOrder(DocumentSortOrderEnum.ASC);
      break;
    case DocumentSortOptionsEnum.ATOZ:
      setDocumentSortOption(DocumentSortOptionsEnum.ATOZ);
      setDocumentSortValue(DocumentSortValueEnum.TITLE);
      setDocumentSortOrder(DocumentSortOrderEnum.ASC);
      break;
    case DocumentSortOptionsEnum.ZTOA:
      setDocumentSortOption(DocumentSortOptionsEnum.ZTOA);
      setDocumentSortValue(DocumentSortValueEnum.TITLE);
      setDocumentSortOrder(DocumentSortOrderEnum.DESC);
      break;
    default:
      setDocumentSortOption(DocumentSortOptionsEnum.NEWEST);
      setDocumentSortValue(DocumentSortValueEnum.DATE);
      setDocumentSortOrder(DocumentSortOrderEnum.DESC);
      break;
  }
}

/**
 * Fetch podcast documents based on specific reasons.
 *
 * @param {string} reason - Reason for fetching documents.
 * @param {DocumentType[]} allDocuments - Current list of all documents.
 * @param {string} feedUrl - Feed URL of the podcast.
 * @param {string} podcastId - ID of the podcast.
 * @param {string} debouncedSearchValue - Debounced search value.
 * @param {string} dashboardDocumentsPage - Dashboard documents page.
 * @param {string} season - Season of the podcast.
 * @param {DocumentSortOptionsEnum} documentSortOption - Selected document sort option.
 * @param {DocumentSortValueEnum} documentSortValue - Selected document sort value.
 * @param {DocumentSortOrderEnum} documentSortOrder - Selected document sort order.
 * @param {DocumentType | ''} playerDocument - Current player document.
 * @param {boolean} playSerial - Whether to play documents serially.
 * @param {DocumentType[]} fullDocuments - List of full documents.
 * @param {function} setHasMoreDocuments - Function to set whether there are more documents.
 * @param {function} resetDashboardDocumentsPage - Function to reset the dashboard documents page.
 * @param {function} increaseDashboardDocumentsPage - Function to increase the dashboard documents page.
 * @param {function} setLoadingDocuments - Function to set the loading state of documents.
 * @param {function} setAllDocuments - Function to set the list of all documents.
 * @param {function} setFullSerialDocuments - Function to set the list of full serial documents.
 * @param {function} onShowPlayButton - Function to show or hide the play button.
 * @param {function} setIsPodcastUnavailable - Function to set whether the podcast is unavailable.
 * @param {function} forceReload - Function to force reload podcasts from Database.
 */
export async function fetchPodcastDocuments(
  reason: string,
  allDocuments: DocumentType[],
  feedUrl: string,
  podcastId: string,
  debouncedSearchValue: string,
  dashboardDocumentsPage: number,
  season: string,
  documentSortOption: DocumentSortOptionsEnum,
  documentSortValue: DocumentSortValueEnum,
  documentSortOrder: DocumentSortOrderEnum,
  playerDocument: DocumentType | '',
  playSerial: boolean,
  fullDocuments: DocumentType[],
  setHasMoreDocuments: (hasMoreDocuments: boolean) => void,
  resetDashboardDocumentsPage: () => void,
  increaseDashboardDocumentsPage: () => void,
  setLoadingDocuments: (loading: boolean) => void,
  setAllDocuments: (documents: DocumentType[]) => void,
  setFullSerialDocuments: (documents: DocumentType[]) => void,
  onShowPlayButton: (showPlayButton: boolean) => void,
  setPodcastSeasons: (seasons: string[]) => void,
  setIsPodcastUnavailable: (isPodcastUnavailable: boolean) => void,
  forceReload?: boolean
) {
  try {
    if (firebase.auth.currentUser) {
      const token = await firebase.auth.currentUser.getIdToken();
      const response = await fetchDocuments(
        token,
        feedUrl,
        podcastId,
        debouncedSearchValue,
        dashboardDocumentsPage,
        DOCUMENTS_PAGE_SIZE,
        season,
        documentSortOption,
        documentSortValue,
        documentSortOrder,
        forceReload
      );

      if (response.data.documents.length === 0) {
        setHasMoreDocuments(false);
        if (reason === FetchDocumentsReasonEnum.FILTER) {
          setAllDocuments([]);
        }
        return;
      }

      let documents = getDocumentsWithUniqueIdentifier(response.data.documents);
      const hasMoreDocuments = hasMoreDocumentsCheck(response.data.documents);
      setHasMoreDocuments(hasMoreDocuments);

      if (response.data.status === 'error') {
        setIsPodcastUnavailable(true);
        setHasMoreDocuments(false);
        setAllDocuments(documents);
        setPodcastSeasons(response.data.allSeasons);
      } else {
        if (response.data.status !== 'unauthorized') {
          let fullDocumentsArray: DocumentType[] = [];

          if (reason === FetchDocumentsReasonEnum.FILTER) {
            const fullDocumentsWithoutCurrent = getOnlyFullDocuments(documents);
            fullDocumentsArray = playerDocument
              ? [playerDocument, ...fullDocumentsWithoutCurrent]
              : fullDocumentsWithoutCurrent;
          } else if (reason === FetchDocumentsReasonEnum.LOAD_MORE) {
            documents = [...allDocuments, ...documents];
            fullDocumentsArray = getOnlyFullDocuments(documents);
          }

          setAllDocuments(documents);
          setFullSerialDocuments(fullDocumentsArray);
          setPodcastSeasons(response.data.allSeasons);

          if (!playerDocument) {
            if (fullDocumentsArray.length > 0) {
              onShowPlayButton(true);
            } else {
              onShowPlayButton(false);
            }
          } else {
            if (
              fullDocumentsArray.length > 0 &&
              playerDocument.uuid !== fullDocumentsArray[0].uuid
            ) {
              onShowPlayButton(true);
            } else {
              onShowPlayButton(false);
            }
          }

          if (playerDocument === '') {
            setFullSerialDocuments(fullDocumentsArray);
            setAllSeasons(response.data.allSeasons);
            if (playSerial) {
              setPlaybackPlaylists({
                documents: fullDocuments
              });
            }
          }
        }
        setIsPodcastUnavailable(false);
      }
    }
  } catch (error) {
    console.error('Error fetching podcast documents:', error);
    setHasMoreDocuments(false);
    setIsPodcastUnavailable(true);
  } finally {
    switch (reason) {
      case FetchDocumentsReasonEnum.FILTER:
        resetDashboardDocumentsPage();
        break;
      case FetchDocumentsReasonEnum.LOAD_MORE:
        increaseDashboardDocumentsPage();
        setLoadingDocuments(false);
        break;
    }
  }
}
